import { Bounce } from "react-toastify";
import { notificationStatusUtils } from "@centivo-business/utils";

export const {
    NotificationStatus,
    NOTIFICATION_QUEUE_ERROR_STATUSES,
    getNotificationDisplayTimestamp,
    isActionablePendingNotificationStatus,
    isPendingLikeNotificationStatus,
} = notificationStatusUtils;

/**
 * Shared `react-toastify` options for error toasts on the notifications page (e.g. queue total count, bulk CSV export).
 * @type {import("react-toastify").ToastOptions}
 */
export const NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS = Object.freeze({
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
    transition: Bounce,
});

/**
 * Notification queue send type (`sendType` on notification rows / API): Automated vs Manual.
 * Matches backend values `"automated"` | `"manual"`.
 * @readonly
 * @enum {string}
 */
export const SendType = Object.freeze({
    AUTOMATED: "automated",
    MANUAL: "manual",
});

/**
 * Status filter for the queue (segmented All / Pending / Sent / Errors).
 * ALL is null so the API can use "no extra filter" without a magic string.
 * @readonly
 * @enum {string | null}
 */
export const QueueFilter = Object.freeze({
    /** All statuses for the current send type. */
    ALL: null,
    SENT: "sent",
    /** Maps to API `status=pending` (v3: {@link NotificationStatus.PENDING} only; {@link NotificationStatus.CORRESPONDENCE_HRP_UPDATE_PENDING} appears under {@link QueueFilter.ALL}). */
    PENDING: "pending",
    /** Maps to API `status=errors` (v3: {@link NOTIFICATION_QUEUE_ERROR_STATUSES}). {@link NotificationStatus.CANCELED} appears under {@link QueueFilter.ALL}. */
    ERRORS: "errors",
});

/**
 * First segment of TanStack `queryKey` arrays for the notification queue (list, count, invalidation).
 * Full keys are built as `[NOTIFICATIONS_QUERY_KEY_ROOT, …]` (see {@link useNotifications}, {@link useTotalCount}).
 * @constant {string}
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-keys
 */
export const NOTIFICATIONS_QUERY_KEY_ROOT = "notifications";

/**
 * First segment of TanStack `queryKey` for `GET /notifications/details` (drawer).
 * Full key: `[NOTIFICATION_DETAILS_QUERY_KEY_ROOT, notificationId]`.
 * @constant {string}
 */
export const NOTIFICATION_DETAILS_QUERY_KEY_ROOT = "notification-details";

/**
 * Optional column filters backed by the DynamoDB GSI composite sort-key combinations.
 * Keys match query-string / API param names (e.g. `recipientid` → list row `recipientId`).
 * @readonly
 * @type {readonly { key: string, label: string, placeholder: string }[]}
 */
export const NOTIFICATION_COLUMN_FILTERS = Object.freeze([
    { key: "client", label: "Client", placeholder: "ACTBL" },
    { key: "template", label: "Template", placeholder: "PP_CO_15" },
    { key: "type", label: "Type", placeholder: "APP" },
    { key: "plan", label: "Plan", placeholder: "ACTBLIZPP" },
    {
        key: "recipientid",
        label: "Member ID",
        placeholder: "f0000000",
    },
    {
        key: "eligibilitytype",
        label: "Eligibility Type",
        placeholder: "Self",
    },
]);

/**
 * Stable DOM `id` for Automated / Manual tab buttons (`aria-labelledby` on the send-type tab panel).
 * @param {string} sendType Send type value (`automated` | `manual`).
 * @returns {string} e.g. `notifications-send-type-tab-automated`.
 */
export function getSendTypeTabButtonId(sendType) {
    return `notifications-send-type-tab-${sendType}`;
}

/**
 * Column label for a single notification (details sheet / single-row CSV). Uses the queue tab
 * when not All; on All, derives from notification status.
 *
 * @param {string | null} queueFilter {@link QueueFilter} value (`null` is All).
 * @param {unknown} status Notification status from list or details payload.
 * @returns {string}
 */
export function getSentOrScheduledTimestampColumnLabelForNotification(
    queueFilter,
    status,
) {
    if (queueFilter === QueueFilter.SENT) {
        return "Sent At";
    }
    if (
        queueFilter === QueueFilter.PENDING ||
        queueFilter === QueueFilter.ERRORS
    ) {
        return "Scheduled For";
    }

    const normalized = String(status ?? "")
        .trim()
        .toUpperCase();
    if (normalized === NotificationStatus.SENT) {
        return "Sent At";
    }
    if (isPendingLikeNotificationStatus(normalized)) {
        return "Scheduled For";
    }
    return "Scheduled/Sent";
}
